import { View, Text, styled } from 'tamagui'
import { CheckCircle, AlertCircle, Info, AlertTriangle, X } from '@tamagui/lucide-icons'
import { useEffect } from 'react'
import { useToastStore, type Toast } from '@/store/toastStore'

const Container = styled(View, {
  position: 'absolute' as any,
  bottom: 16,
  right: 16,
  zIndex: 3000,
  gap: 8,
  // @ts-expect-error -- web pointer-events
  style: { pointerEvents: 'none' },
})

const typeColors = {
  success: '#3ECF7B',
  error: '#CF3E4F',
  info: '#3ECFCF',
  warning: '#E8A840',
}

const typeIcons = {
  success: CheckCircle,
  error: AlertCircle,
  info: Info,
  warning: AlertTriangle,
}

function ToastCard({ toast }: { toast: Toast }) {
  const removeToast = useToastStore((s) => s.removeToast)
  const color = typeColors[toast.type]
  const Icon = typeIcons[toast.type]

  useEffect(() => {
    const timer = setTimeout(() => removeToast(toast.id), 4000)
    return () => clearTimeout(timer)
  }, [toast.id, removeToast])

  return (
    <View
      flexDirection="row"
      alignItems="center"
      gap={10}
      backgroundColor="$surfaceRaised"
      borderRadius="$borderRadiusMd"
      borderLeftWidth={3}
      borderLeftColor={color}
      borderWidth={1}
      borderColor="$borderDefault"
      paddingHorizontal={12}
      paddingVertical={10}
      minWidth={260}
      maxWidth={360}
      // @ts-expect-error -- web pointer-events + shadow
      style={{
        pointerEvents: 'auto',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
        animation: 'slideInRight 200ms ease-out',
      }}
    >
      <Icon size={16} color={color} />
      <Text
        fontFamily="$fontMono"
        fontSize={13}
        color="$surfaceForeground"
        flex={1}
      >
        {toast.message}
      </Text>
      <View
        width={20}
        height={20}
        borderRadius="$borderRadiusSm"
        alignItems="center"
        justifyContent="center"
        cursor="pointer"
        hoverStyle={{ backgroundColor: '$sidebarHover' }}
        onPress={() => removeToast(toast.id)}
      >
        <X size={12} color="$sidebarMuted" />
      </View>
    </View>
  )
}

export function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts)

  if (toasts.length === 0) return null

  return (
    <Container>
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} />
      ))}
    </Container>
  )
}
